import React, { useMemo } from 'react'

import { AnnotationQueryVariablesProvider } from '@sketch/modules-common'
import {
  CanvasInteractionsProvider,
  CanvasSelectionProvider,
} from '@sketch/canvas'

import { CanvasFocus, CanvasScopeProvider } from '../../scope'
import { CompareProvider } from '../../../versioning/compare'
import { CanvasLeftPanelResizingProvider } from '../CanvasLeftPanel'
import { CanvasAnnotationOverlayContext } from '../CanvasAnnotationOverlayContext'
import { FrameInspectorProvider } from '../../../shares/FrameView/components/FrameInspectorProvider'
import { GridsAndLayoutsProvider } from '../../../shares/components/GridsAndLayoutsProvider'

interface CanvasProvidersProps {
  /** @see CanvasScopeProvider */
  pageUUID: string
  /** @see useCanvasFocus */
  focus: CanvasFocus
  /** Absent in a Preview, which annotates without one. */
  shareIdentifier?: string
  versionIdentifier?: string
  isPreview?: boolean
  /** Whether annotations can be placed and are drawn at all. */
  annotationsDisabled: boolean
  isViewingLatestVersion: boolean
  children: React.ReactNode
}

/**
 * Everything the canvas reads from context, for either host.
 *
 * A Share and a Preview mounted the same eight providers each, in different
 * orders and split across different components - five of which take nothing but
 * children, so the nesting carried no information. What actually differs
 * between the two hosts is the three values below, and they are now the only
 * thing either host has to say.
 *
 * The order here is not meaningful except at the ends: the scope is outermost
 * because the annotation overlay reads it, and the overlay is innermost because
 * it needs the annotation query variables above it.
 */
export function CanvasProviders({
  pageUUID,
  focus,
  shareIdentifier,
  versionIdentifier,
  isPreview,
  annotationsDisabled,
  isViewingLatestVersion,
  children,
}: CanvasProvidersProps) {
  const subjects = useMemo(
    () => (pageUUID ? [{ type: 'PAGE' as const, permanentId: pageUUID }] : []),
    [pageUUID]
  )

  return (
    <CanvasScopeProvider pageUUID={pageUUID} focus={focus}>
      {/* Outside the canvas and the sidebar alike: both show the comparison,
      they sit in different parts of the tree, and it costs two file loads to
      work out - so it is run once, here. */}
      <CompareProvider pageUUID={pageUUID || undefined}>
        <CanvasInteractionsProvider>
          <CanvasSelectionProvider>
            <CanvasLeftPanelResizingProvider>
              <FrameInspectorProvider>
                <GridsAndLayoutsProvider>
                  <AnnotationQueryVariablesProvider
                    shareIdentifier={shareIdentifier}
                    versionIdentifier={versionIdentifier}
                    subjects={subjects}
                    isPreview={isPreview}
                  >
                    <CanvasAnnotationOverlayContext
                      disabled={annotationsDisabled}
                      isViewingLatestVersion={isViewingLatestVersion}
                    >
                      {children}
                    </CanvasAnnotationOverlayContext>
                  </AnnotationQueryVariablesProvider>
                </GridsAndLayoutsProvider>
              </FrameInspectorProvider>
            </CanvasLeftPanelResizingProvider>
          </CanvasSelectionProvider>
        </CanvasInteractionsProvider>
      </CompareProvider>
    </CanvasScopeProvider>
  )
}
